import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Lock, Crown, Sparkles, ShieldCheck, AlertCircle, RefreshCw } from "lucide-react";
import { Header } from "@/pages/Vitals";
import { toast } from "sonner";

const FREE_FEATURES = [
  "Calendar dashboard (Day / Month / Year)",
  "1 family member profile (besides you)",
  "Vitals, symptoms, diagnostics, consultations & medications",
  "AI symptom search (Claude Sonnet 4.5)",
  "Prescription & report OCR (GPT vision)",
  "Voice & AI field assist (elder-friendly)",
];
const PRO_FEATURES = [
  "Unlimited family members",
  "Share with doctor — secure PDF exports & private links",
  "Trend charts (BP, sugar, weight, more)",
  "Medication adherence analytics",
  "Priority support",
  "Coming soon: SMS / push medication reminders",
];

export default function Subscription() {
  const { user, login } = useAuth();
  const [search] = useSearchParams();
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);
  const sessionIdFromUrl = search.get("session_id");
  const cancelled = search.get("cancelled");

  const load = useCallback(async () => {
    const r = await api.get("/me/subscription");
    setSub(r.data);
    // Sync tier into local auth context
    if (user && r.data.tier !== user.tier) {
      login({ ...user, tier: r.data.tier });
    }
  }, [user, login]);

  // Polling after Stripe redirect
  const pollStatus = useCallback(async (sid, attempts = 0) => {
    const max = 8, interval = 2000;
    if (attempts >= max) {
      setStatusMsg({ type: "warn",
        text: "Still processing. Stripe webhooks may take up to a minute. Refresh this page to see the latest status." });
      return;
    }
    try {
      const r = await api.get(`/payments/status/${sid}`);
      if (r.data.payment_status === "paid" || r.data.tier === "pro") {
        setStatusMsg({ type: "ok", text: "Welcome to Pro! Your account has been upgraded." });
        toast.success("Welcome to Verda Pro 🎉");
        load();
        return;
      }
      if (r.data.status === "expired") {
        setStatusMsg({ type: "err", text: "Checkout session expired. Please try again." });
        return;
      }
      setStatusMsg({ type: "pending", text: "Verifying your payment…" });
      setTimeout(() => pollStatus(sid, attempts + 1), interval);
    } catch (e) {
      setStatusMsg({ type: "err", text: e?.response?.data?.detail || "Could not verify payment." });
    }
  }, [load]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (sessionIdFromUrl) {
      setStatusMsg({ type: "pending", text: "Checking your payment status…" });
      pollStatus(sessionIdFromUrl);
    } else if (cancelled) {
      setStatusMsg({ type: "warn", text: "Checkout cancelled. No charge was made." });
    }
  }, [sessionIdFromUrl, cancelled, pollStatus]);

  const subscribe = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/checkout", {
        package_id: "pro_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const tier = sub?.tier || user?.tier || "free";

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <Header title="Subscription" subtitle="Pick a plan that suits your family." />

      {statusMsg && <StatusBanner msg={statusMsg} onRetry={() => sessionIdFromUrl && pollStatus(sessionIdFromUrl)} />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* FREE */}
        <Card className={`p-8 rounded-2xl border-2 ${tier === "free" ? "border-[#4A6750] bg-[#FFFFFF]" : "border-[#E5E7E4] bg-white"}`} data-testid="plan-free">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#7D8F84]">Base</p>
              <h2 className="font-heading text-3xl font-semibold tracking-tight mt-1">Free</h2>
              <p className="text-[#5C6D63] mt-2">Everything you need to manage daily health.</p>
            </div>
            {tier === "free" && (
              <span className="rounded-full bg-[#E8ECE9] text-[#4A6750] text-xs font-medium px-3 py-1.5">Current plan</span>
            )}
          </div>
          <div className="my-6">
            <span className="font-heading text-3xl font-semibold text-[#5C6D63]">No account cost</span>
            <p className="text-xs text-[#7D8F84] mt-2">Log daily health without a subscription.</p>
          </div>
          <ul className="space-y-2.5">
            {FREE_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-[#1C2E24]">
                <Check className="w-4 h-4 text-[#5B8266] flex-shrink-0 mt-0.5" strokeWidth={2.25} />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <Button disabled className="w-full mt-6 rounded-xl bg-[#E8ECE9] text-[#4A6750] hover:bg-[#E8ECE9]" data-testid="free-cta">
            You&apos;re on Free
          </Button>
        </Card>

        {/* PRO */}
        <Card className={`p-8 rounded-2xl border-2 ${tier === "pro" ? "border-[#C76B58]" : "border-[#4A6750]/30"} bg-gradient-to-br from-white to-[#F4F6F2] relative overflow-hidden`} data-testid="plan-pro">
          {tier !== "pro" && (
            <div className="absolute top-5 right-5">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#C76B58]/10 text-[#C76B58] text-xs font-semibold uppercase tracking-wider px-3 py-1.5">
                <Sparkles className="w-3 h-3" /> Test mode
              </span>
            </div>
          )}
          {tier === "pro" && (
            <div className="absolute top-5 right-5">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#C76B58] text-white text-xs font-semibold uppercase tracking-wider px-3 py-1.5">
                <Crown className="w-3 h-3" /> Active
              </span>
            </div>
          )}
          <div>
            <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#7D8F84]">Premium</p>
            <h2 className="font-heading text-3xl font-semibold tracking-tight mt-1 flex items-center gap-2">
              <Crown className="w-7 h-7 text-[#C76B58]" strokeWidth={1.5} /> Pro
            </h2>
            <p className="text-[#5C6D63] mt-2">Deeper insights, full family, no caps.</p>
          </div>
          <div className="my-6">
            <span className="font-heading text-3xl font-semibold text-[#5C6D63]">Special early-access pricing</span>
            <p className="text-xs text-[#7D8F84] mt-2">Contact us to unlock Pro during our launch window.</p>
          </div>
          <ul className="space-y-2.5">
            {PRO_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-[#1C2E24]">
                <Check className="w-4 h-4 text-[#5B8266] flex-shrink-0 mt-0.5" strokeWidth={2.25} />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          {tier === "pro" ? (
            <p className="text-xs text-[#5B8266] mt-6 text-center flex items-center justify-center gap-1.5">
              <ShieldCheck className="w-4 h-4" /> Pro active{sub?.pro_since ? ` since ${new Date(sub.pro_since).toLocaleDateString()}` : ""}
            </p>
          ) : (
            <a href={`mailto:care@verdahealth.com?subject=Pro%20access%20request&body=Hi%20Verda%20Health%2C%0AI%27d%20like%20early%20access%20to%20Pro.%0A%0AName%3A%20${encodeURIComponent(user?.name || "")}%0APhone%3A%20${encodeURIComponent(user?.phone || "")}`}
               className="block w-full mt-6" data-testid="pro-cta">
              <Button className="w-full rounded-xl bg-[#1C2E24] hover:bg-[#0F1A14] text-white font-medium">
                <Lock className="w-4 h-4 mr-2" /> Request Pro access
              </Button>
            </a>
          )}
          <p className="text-[10px] text-[#7D8F84] mt-3 text-center">
            Priority onboarding · No commitment yet
          </p>
        </Card>
      </div>
    </div>
  );
}

function StatusBanner({ msg, onRetry }) {
  const palette = {
    ok:      { bg: "#5B8266", icon: ShieldCheck },
    err:     { bg: "#C76B58", icon: AlertCircle },
    warn:    { bg: "#D8A24A", icon: AlertCircle },
    pending: { bg: "#5B7582", icon: RefreshCw },
  };
  const p = palette[msg.type] || palette.pending;
  const Icon = p.icon;
  return (
    <Card className="p-4 rounded-2xl border-2 flex items-center gap-3" style={{ borderColor: `${p.bg}55`, background: `${p.bg}0d` }} data-testid="status-banner">
      <Icon className={`w-5 h-5 flex-shrink-0 ${msg.type === "pending" ? "animate-spin" : ""}`} style={{ color: p.bg }} />
      <p className="text-sm flex-1" style={{ color: "#1C2E24" }}>{msg.text}</p>
      {msg.type === "warn" && onRetry && (
        <Button size="sm" variant="outline" onClick={onRetry} className="rounded-lg">Check again</Button>
      )}
    </Card>
  );
}
